import './MeetingSchedulingWeekView.scss';
import React, { useEffect, useMemo, useState, useRef } from 'react';
import moment from 'moment';
import {
    MeetingFormat,
    MeetingFormatTypesOptions,
} from '../../../../slices/marketplace/meetings/models/enums/meeting-format.enum';
import { MeetingAttendanceMode } from '../../../../slices/marketplace/meetings/models/enums/meeting-attendance-mode.enum';
import { MeetingParticipantsType } from '../../../../slices/marketplace/meetings/models/enums/meeting-participant-type.enum';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faEdit,
    faCheck,
    faTimes,
    faCalendar,
} from '@fortawesome/free-solid-svg-icons';
import { faCommentDots, faUser } from '@fortawesome/free-regular-svg-icons';
import ConfirmationModal from '../../../../components/Modal/ConfirmationModal';
import { CalendarObject } from '../../../../slices/marketplace/meetings/models/calendar-object.model';
import { AvailabilityBlockObject } from '../../../../slices/marketplace/meetings/models/availability-block-object.model';
import { MeetingObject } from '../../../../slices/marketplace/meetings/models/meeting-object.model';
import { MeetingProposalStatus } from '../../../../slices/marketplace/meetings/models/enums/meeting-proposal-status.enum';
import { EngagementMeetingStatus } from '../../../../slices/marketplace/meetings/models/enums/meeting-status.enum';
import { TimelineParty } from '../../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import MeetingParticipantsModal from '../MeetingParticipantsModal/MeetingParticipantsModal';
import { useNavigate } from 'react-router-dom';
import { paths } from '../../../../routing/path';
import { QueryParams } from '../../../../utils/queryParams';
import { MarketplaceCommsHubTab } from '../../../../containers/AdminMarketplace/MarketplaceTabs/MarketplaceCommsHub/MarketplaceCommsHubTabs';
import { SellerMarketplaceTab } from '../../../../slices/marketplace/timeline/models/enums/seller-marketplace-tab.enum';
import { BuyerOppMarketplaceTab } from '../../../../slices/marketplace/timeline/models/enums/buyer-opportunity-details-tab.enum';
import { useCurrentMarketplaceParty } from '../../../../custom-hooks/useCurrentMarketplaceParty';
import { useStateSelector } from '../../../../store/selectors';
import { USER_ROLES } from '../../../../utils/constants';
import ReactSwitch from 'react-switch';
import { EngagementProcessStatus } from '../../../../slices/marketplace/timeline/models/enums/engagement-process-status.enum';
import {
    canBuyerTeamMemberJoinMeeting,
    canBuyerTeamMemberManageMeetings,
    canSellerTeamMemberManageMeetings,
    isSellerTeamMemberForEngagement,
} from '../../../../utils/meetingSchedulingHelper';
import { NotificationFlag } from '../../../../components/NotificationFlag/NotificationFlag';
import { EngagementBuyer } from '../../../../slices/marketplace/timeline/models/engagement-buyer.model';
import EngagementMeetingFullInfoModal from './EngagementMeetingFullInfoModal/EngagementMeetingFullInfoModal';
import InfoTooltip from '../../../../components/info-tooltip/info-tooltip';
import { MeetingTimeZoneHelper } from '../../../../utils/meetingTimeZoneHelper';
import { OverlappingDayCalendarView } from './OverlappingDayCalendarView';
import EngagementMeetingCancelModal from './EngagementMeetingCancelModal/EngagementMeetingCancelModal';

function isAvailabilityBlockObject(
    obj: CalendarObject
): obj is AvailabilityBlockObject {
    const a = obj as any;
    const formats = a?.formats ?? a?.Formats;
    return Array.isArray(formats);
}

function isOtherGhostMeetingObject(obj: CalendarObject): boolean {
    if (isAvailabilityBlockObject(obj)) {
        return false;
    }
    const meeting = obj as MeetingObject;
    return meeting.isOtherGhostMeeting === true;
}

interface MeetingSchedulingWeekViewProps {
    startDate?: Date;
    endDate?: Date;
    events: CalendarObject[];
    selectedDate?: Date;
    setSelectedDate?: (date: Date) => void;
    selectedEvent?: CalendarObject;
    setSelectedEvent?: React.Dispatch<
        React.SetStateAction<CalendarObject | undefined>
    >;
    highlightedMeetingId?: number;
    shouldScrollToCurrentTime?: boolean;
    showDebugnavButtons?: boolean;
    onEmptySlotClick?: (date: Date, startTime: Date, endTime: Date) => void;
    viewMode: 'buyerTopLevel' | 'sellerTopLevel' | 'engagement' | 'opportunity';
    onDiscussionClick?: (meetingId: number) => void;
    onEditClick?: (eventIdOrProposalId: number | string) => void;
    onParticipantsClick?: (meetingId: number) => void;
    onDeleteClick?: (meetingId: number) => void;
    party?: TimelineParty;
    onProposalAccept?: (meetingProposalId: string) => Promise<void>;
    onBuyerProposalAccept?: (meetingProposalId: string) => void;
    onProposalCancel?: (meetingProposalId: string) => Promise<void>;
    engagementId?: number;
    onRefreshMeetings?: () => void;
    showScheduledMeetings?: boolean;
    setShowScheduledMeetings?: (value: boolean) => void;
    isLoadingGhostMeetings?: boolean;
    /** When true, calendar clicks do not change selection (e.g. buyer has pending proposal). */
    selectionDisabled?: boolean;
    /** When set (e.g. user clicked empty slot to propose alternative), highlight this range in the week view. */
    selectedTimeSlot?: { startTime: Date; endTime: Date };
    /** Build notification flag code for a meeting/proposal event to show badge on tile. */
    getNotificationFlagCodeForEvent?: (
        event: MeetingObject
    ) => string | undefined;
    showPostAvailabilityButton?: boolean;
    onPostAvailabilityClick?: () => void;
    isPostingAvailability?: boolean;
    selectedBuyers?: EngagementBuyer[];
    practiceName?: string;
    isSfas?: boolean;
    isActiveBroker?: boolean;
}

interface TimeSlot {
    hour: number;
    label: string;
}

interface DayColumn {
    date: Date;
    dayName: string;
    monthDay: string;
}

const HOUR_HEIGHT_PX = 120;
const SCROLL_SETTLED_FRAME_COUNT = 5;
const MAX_PROGRAMMATIC_SCROLL_MS = 3000;

export const MeetingSchedulingWeekView = (
    props: MeetingSchedulingWeekViewProps
) => {
    const navigate = useNavigate();
    const party = useCurrentMarketplaceParty(props.engagementId);
    const summary = useStateSelector((state) => state.userSummary.summary);
    const currentEngagement = useStateSelector(
        (state) => state.timelines.currentTimeline
    );
    const isAdmin = summary?.roleId === USER_ROLES.ADMIN;
    const isBroker = summary?.isMarketplaceBroker;
    const isObserver = party === TimelineParty.Observer;
    const isBuyer = party === TimelineParty.Buyer;

    const isEngagementLocked = (() => {
        const status = currentEngagement?.engagementProcess?.status;
        return (
            status === EngagementProcessStatus.Suspended ||
            status === EngagementProcessStatus.Terminated ||
            status === EngagementProcessStatus.Completed
        );
    })();

    const hasMeetingStarted = (event: CalendarObject): boolean => {
        if (!event?.dateStart) return false;
        const meetingStartTime =
            event.dateStart instanceof Date
                ? event.dateStart
                : new Date(event.dateStart);
        return meetingStartTime <= new Date();
    };
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const headerScrollRef = useRef<HTMLDivElement>(null);
    const bodyScrollRef = useRef<HTMLDivElement>(null);
    const isProgrammaticScrollRef = useRef(false);
    const scrollSettleFrameRef = useRef<number | null>(null);
    const [columnWidth, setColumnWidth] = useState<number>(350);
    const [showApproveConfirmation, setShowApproveConfirmation] =
        useState(false);
    const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
    const [selectedMeetingForFullInfo, setSelectedMeetingForFullInfo] =
        useState<MeetingObject | null>(null);
    const [pendingProposal, setPendingProposal] = useState<
        MeetingObject | undefined
    >(undefined);
    const [isApproving, setIsApproving] = useState(false);
    const [isCancelling, setIsCancelling] = useState(false);
    const [isParticipantsModalVisible, setIsParticipantsModalVisible] =
        useState(false);
    const [selectedMeetingForParticipants, setSelectedMeetingForParticipants] =
        useState<MeetingObject | null>(null);
    const showScheduledMeetings = props.showScheduledMeetings ?? false;
    const setShowScheduledMeetings = props.setShowScheduledMeetings;
    const [
        showPostAvailabilityConfirmation,
        setShowPostAvailabilityConfirmation,
    ] = useState(false);
    const [overlappingDayPopupDate, setOverlappingDayPopupDate] =
        useState<Date | null>(null);

    const activeBuyers = useMemo(() => {
        // Use props.selectedBuyers as the base list
        const baseBuyers = props.selectedBuyers || [];

        // Get NDA status from currentEngagement if available
        const engagementBuyers =
            currentEngagement?.engagementProcess?.buyers || [];

        // Create a map of buyerId to NDA status from engagement
        const ndaStatusMap = new Map(
            engagementBuyers.map((buyer) => [
                buyer.buyerId,
                buyer.isDataNdaSigned,
            ])
        );

        // Filter buyers that are active and have signed NDA
        return baseBuyers.filter((buyer) => {
            // Check if buyer is active
            if (!buyer.isActive) return false;

            // Check NDA status from engagement first, fallback to buyer's own status
            const hasSignedNda = ndaStatusMap.has(buyer.buyerId)
                ? ndaStatusMap.get(buyer.buyerId)
                : buyer.isDataNdaSigned;

            return hasSignedNda;
        });
    }, [currentEngagement, props.selectedBuyers]);

    const getBuyerName = (event: MeetingObject): string => {
        const buyerId = event.buyerId ?? event.posterId;
        const buyer = activeBuyers?.find(
            (b) => Number(b.buyerId) === Number(buyerId)
        );
        return (
            buyer?.companyName ||
            buyer?.buyerAccountName ||
            event.posterName ||
            `Buyer ${buyerId}`
        );
    };

    const [timezone] = useState<string>(() => {
        var zone_name = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const formatter = new Intl.DateTimeFormat('en-US', {
            timeZone: zone_name,
            timeZoneName: 'short',
        });
        const parts = formatter.formatToParts(new Date());
        const timeZoneNamePart = parts.find(
            (part) => part.type === 'timeZoneName'
        );

        return timeZoneNamePart ? timeZoneNamePart.value : zone_name;
    });

    const displayEvents = useMemo(() => {
        let events = props.events || [];

        if (props.startDate && props.endDate) {
            const rangeStart = moment(props.startDate).startOf('day');
            const rangeEnd = moment(props.endDate).endOf('day');

            events = events.filter((event) => {
                const eventStart = moment(event.dateStart).startOf('day');
                const eventEnd = moment(event.dateEnd).endOf('day');

                return (
                    eventStart.isSameOrBefore(rangeEnd, 'day') &&
                    eventEnd.isSameOrAfter(rangeStart, 'day')
                );
            });
        }

        return events;
    }, [props.events, props.startDate, props.endDate]);

    const timeSlots = useMemo<TimeSlot[]>(() => {
        const slots: TimeSlot[] = [];
        for (let hour = 0; hour < 24; hour++) {
            const isPM = hour >= 12;
            const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
            const period = isPM ? 'PM' : 'AM';
            slots.push({
                hour,
                label: `${displayHour} ${period}`,
            });
        }
        return slots;
    }, []);

    const dayColumns = useMemo<DayColumn[]>(() => {
        const days: DayColumn[] = [];

        if (props.startDate && props.endDate) {
            const minStart = moment(props.startDate).startOf('day');
            const maxEnd = moment(props.endDate).startOf('day');
            let current = minStart.clone();

            while (current.isSameOrBefore(maxEnd, 'day')) {
                days.push({
                    date: current.toDate(),
                    dayName: current.format('dddd'),
                    monthDay: current.format('MMM D'),
                });
                current.add(1, 'day');
            }

            return days;
        }

        const baseDate = props.selectedDate || new Date();
        let startDay = moment(baseDate).startOf('day');

        if (props.startDate) {
            const minStart = moment(props.startDate).startOf('day');
            if (startDay.isBefore(minStart, 'day')) {
                startDay = minStart.clone();
            }
        }

        const daysToShow = props.endDate ? undefined : 7; // week if no endDate
        let dayCount = 0;
        let current = startDay.clone();

        while (true) {
            if (props.endDate) {
                const maxEnd = moment(props.endDate).startOf('day');
                if (current.isAfter(maxEnd, 'day')) {
                    break;
                }
            } else if (daysToShow && dayCount >= daysToShow) {
                break;
            }

            days.push({
                date: current.toDate(),
                dayName: current.format('dddd'),
                monthDay: current.format('MMM D'),
            });

            current.add(1, 'day');
            dayCount++;
        }

        return days;
    }, [props.selectedDate, props.startDate, props.endDate]);

    const getEventsForSlot = (date: Date, hour: number): CalendarObject[] => {
        const dayStart = moment(date).startOf('day').add(hour, 'hours');
        const dayEnd = dayStart.clone().add(1, 'hour');

        return displayEvents.filter((event) => {
            const eventStart = moment(event.dateStart);
            const eventEnd = moment(event.dateEnd);

            return eventStart.isBefore(dayEnd) && eventEnd.isAfter(dayStart);
        });
    };

    // Get all events for a specific day to check overlaps
    const getEventsForDay = (date: Date): CalendarObject[] => {
        const dayStart = moment(date).startOf('day');
        const dayEnd = dayStart.clone().endOf('day');

        return displayEvents
            .filter((event) => {
                const eventStart = moment(event.dateStart);
                const eventEnd = moment(event.dateEnd);

                return (
                    eventStart.isBefore(dayEnd) && eventEnd.isAfter(dayStart)
                );
            })
            .sort((a, b) => moment(a.dateStart).diff(moment(b.dateStart)));
    };

    // Returns true if any two meetings/proposals on the day intersect (availability blocks excluded, status ignored)
    const hasDayOverlappingMeetings = (date: Date): boolean => {
        const items = getEventsForDay(date).filter(
            (e) => !isAvailabilityBlockObject(e)
        );
        for (let i = 0; i < items.length; i++) {
            for (let j = i + 1; j < items.length; j++) {
                const aStart = moment(items[i].dateStart);
                const aEnd = moment(items[i].dateEnd);
                const bStart = moment(items[j].dateStart);
                const bEnd = moment(items[j].dateEnd);
                if (aStart.isBefore(bEnd) && aEnd.isAfter(bStart)) {
                    return true;
                }
            }
        }
        return false;
    };

    // Returns meetings/proposals involved in at least one overlap on the given day
    const getDayOverlappingMeetings = (date: Date): CalendarObject[] => {
        const items = getEventsForDay(date).filter(
            (e) => !isAvailabilityBlockObject(e)
        );
        const involved = new Set<number>();
        for (let i = 0; i < items.length; i++) {
            for (let j = i + 1; j < items.length; j++) {
                const aStart = moment(items[i].dateStart);
                const aEnd = moment(items[i].dateEnd);
                const bStart = moment(items[j].dateStart);
                const bEnd = moment(items[j].dateEnd);
                if (aStart.isBefore(bEnd) && aEnd.isAfter(bStart)) {
                    involved.add(items[i].id);
                    involved.add(items[j].id);
                }
            }
        }
        return items.filter((e) => involved.has(e.id));
    };

    const getDayOverlapTooltipHtml = (date: Date): string => {
        const meetings = getDayOverlappingMeetings(date);
        const rows = meetings
            .map(
                (m) =>
                    `<div class="overlap-tooltip-row">${moment(m.dateStart).format('h:mm A')} – ${MeetingTimeZoneHelper.formatTime(m.dateEnd)}</div>`
            )
            .join('');
        return `<div class="overlap-tooltip-content"><strong>Concurrent meetings:</strong>${rows}</div>`;
    };

    // Calculate overlap groups for positioning (exclude availability blocks from column calculation)
    const getEventPosition = (
        event: CalendarObject,
        dayEvents: CalendarObject[]
    ) => {
        // Availability blocks always take full width
        if (isAvailabilityBlockObject(event)) {
            return { column: 0, totalColumns: 1 };
        }

        const isGhostMeeting = isOtherGhostMeetingObject(event);

        const candidateEvents = dayEvents.filter((e) => {
            if (isAvailabilityBlockObject(e)) return false; // Exclude availability blocks
            const eIsGhost = isOtherGhostMeetingObject(e);
            return isGhostMeeting === eIsGhost; // Same type (ghost or regular)
        });

        const eventColumns = new Map<CalendarObject, number>();
        const columns: CalendarObject[][] = [];

        const sortedEvents = [...candidateEvents].sort((a, b) =>
            moment(a.dateStart).diff(moment(b.dateStart))
        );

        for (const currentEvent of sortedEvents) {
            const currentStart = moment(currentEvent.dateStart);
            const currentEnd = moment(currentEvent.dateEnd);

            let assignedColumn = -1;
            for (let colIndex = 0; colIndex < columns.length; colIndex++) {
                const hasOverlap = columns[colIndex].some((existingEvent) => {
                    const existingStart = moment(existingEvent.dateStart);
                    const existingEnd = moment(existingEvent.dateEnd);
                    return (
                        existingStart.isBefore(currentEnd) &&
                        existingEnd.isAfter(currentStart)
                    );
                });

                if (!hasOverlap) {
                    assignedColumn = colIndex;
                    break;
                }
            }

            if (assignedColumn === -1) {
                assignedColumn = columns.length;
                columns.push([]);
            }

            columns[assignedColumn].push(currentEvent);
            eventColumns.set(currentEvent, assignedColumn);
        }

        const globalTotalColumns = columns.length;
        const globalColumn = eventColumns.get(event) ?? 0;

        if (globalTotalColumns <= 1) {
            return { column: 0, totalColumns: 1, span: 1 };
        }

        const eventStart = moment(event.dateStart);
        const eventEnd = moment(event.dateEnd);

        const occupiedCols = new Set<number>();
        for (const e of candidateEvents) {
            if (e === event) continue;
            if (
                moment(e.dateStart).isBefore(eventEnd) &&
                moment(e.dateEnd).isAfter(eventStart)
            ) {
                const col = eventColumns.get(e);
                if (col !== undefined) occupiedCols.add(col);
            }
        }

        if (occupiedCols.size === 0) {
            return { column: 0, totalColumns: 1, span: 1 };
        }

        const localTotalColumns = occupiedCols.size + 1;

        let bestColumn = globalColumn;
        let bestSpan = 0;
        for (let startCol = 0; startCol < localTotalColumns; startCol++) {
            if (occupiedCols.has(startCol)) continue;
            let span = 1;
            for (let c = startCol + 1; c < localTotalColumns; c++) {
                if (occupiedCols.has(c)) break;
                span++;
            }
            if (span > bestSpan) {
                bestSpan = span;
                bestColumn = startCol;
            }
        }

        return {
            column: bestColumn,
            totalColumns: localTotalColumns,
            span: bestSpan,
        };
    };

    useEffect(() => {
        const headerScroll = headerScrollRef.current;
        const bodyScroll = bodyScrollRef.current;

        if (!headerScroll || !bodyScroll) return;

        let isScrolling = false;

        const getMaxScrollLeft = (element: HTMLDivElement): number => {
            return Math.max(0, element.scrollWidth - element.clientWidth);
        };

        const getAlignedMaxScroll = (): number => {
            const headerMax = getMaxScrollLeft(headerScroll);
            const bodyMax = getMaxScrollLeft(bodyScroll);
            return Math.min(headerMax, bodyMax);
        };

        const clampScrollLeft = (scrollLeft: number): number => {
            const maxScroll = getAlignedMaxScroll();
            return Math.max(0, Math.min(scrollLeft, maxScroll));
        };

        const syncScroll = (source: HTMLDivElement, scrollLeft: number) => {
            if (isScrolling || isProgrammaticScrollRef.current) return;
            isScrolling = true;

            const clampedScroll = clampScrollLeft(scrollLeft);

            if (headerScroll !== source) {
                headerScroll.scrollLeft = clampedScroll;
            }
            if (bodyScroll !== source) {
                bodyScroll.scrollLeft = clampedScroll;
            }

            requestAnimationFrame(() => {
                isScrolling = false;
            });
        };

        const handleHeaderScroll = () => {
            if (isScrolling) return;

            if (headerScroll) {
                const clampedScroll = clampScrollLeft(headerScroll.scrollLeft);
                if (clampedScroll !== headerScroll.scrollLeft) {
                    headerScroll.scrollLeft = clampedScroll;
                }
                syncScroll(headerScroll, clampedScroll);
            }
        };

        const handleBodyScroll = () => {
            if (isScrolling) return;

            if (bodyScroll) {
                const clampedScroll = clampScrollLeft(bodyScroll.scrollLeft);
                if (clampedScroll !== bodyScroll.scrollLeft) {
                    bodyScroll.scrollLeft = clampedScroll;
                }
                syncScroll(bodyScroll, clampedScroll);
            }
        };

        headerScroll.addEventListener('scroll', handleHeaderScroll, {
            passive: true,
        });
        bodyScroll.addEventListener('scroll', handleBodyScroll, {
            passive: true,
        });

        return () => {
            headerScroll.removeEventListener('scroll', handleHeaderScroll);
            bodyScroll.removeEventListener('scroll', handleBodyScroll);
        };
    }, [dayColumns.length]);

    useEffect(() => {
        const headerScroll = headerScrollRef.current;
        const bodyScroll = bodyScrollRef.current;

        if (
            !headerScroll ||
            !bodyScroll ||
            !props.selectedDate ||
            dayColumns.length === 0
        ) {
            return;
        }

        const selectedMoment = moment(props.selectedDate).startOf('day');
        let dayIndex = dayColumns.findIndex((day) => {
            const dayMoment = moment(day.date).startOf('day');
            return dayMoment.isSame(selectedMoment, 'day');
        });

        if (dayIndex === -1) {
            dayIndex = dayColumns.findIndex((day) =>
                moment(day.date).isSame(selectedMoment, 'month')
            );
        }

        if (dayIndex === -1) {
            return;
        }

        const isStartDate =
            props.startDate &&
            moment(props.selectedDate)
                .startOf('day')
                .isSame(moment(props.startDate).startOf('day'), 'day');
        const isEndDate =
            props.endDate &&
            moment(props.selectedDate)
                .startOf('day')
                .isSame(moment(props.endDate).startOf('day'), 'day');

        const doScroll = () => {
            // (350px desktop, 280px mobile)
            const isMobile = window.innerWidth <= 768;
            const columnWidth = isMobile ? 280 : 350;
            const containerWidth = bodyScroll.clientWidth;

            let scrollLeft: number;

            if (isStartDate) {
                scrollLeft = 0;
            } else if (isEndDate) {
                scrollLeft = bodyScroll.scrollWidth - containerWidth;
            } else {
                const columnLeft = dayIndex * columnWidth;
                scrollLeft = columnLeft - containerWidth / 2 + columnWidth / 2;
                scrollLeft = Math.max(
                    0,
                    Math.min(scrollLeft, bodyScroll.scrollWidth - containerWidth)
                );
            }

            isProgrammaticScrollRef.current = true;

            headerScroll.scrollTo({ left: headerScroll.scrollLeft, behavior: 'instant' as ScrollBehavior });
            bodyScroll.scrollTo({ left: bodyScroll.scrollLeft, behavior: 'instant' as ScrollBehavior });

            requestAnimationFrame(() => {
                headerScroll.scrollTo({ left: scrollLeft, behavior: 'smooth' });
                bodyScroll.scrollTo({ left: scrollLeft, behavior: 'smooth' });
                waitForProgrammaticScrollToSettle();
            });
        };

        const waitForProgrammaticScrollToSettle = () => {
            const startedAt = performance.now();
            let lastHeaderScrollLeft = -1;
            let lastBodyScrollLeft = -1;
            let settledFrameCount = 0;

            const checkIfSettled = () => {
                const hasStopped =
                    headerScroll.scrollLeft === lastHeaderScrollLeft &&
                    bodyScroll.scrollLeft === lastBodyScrollLeft;
                settledFrameCount = hasStopped ? settledFrameCount + 1 : 0;
                lastHeaderScrollLeft = headerScroll.scrollLeft;
                lastBodyScrollLeft = bodyScroll.scrollLeft;

                const hasTimedOut =
                    performance.now() - startedAt > MAX_PROGRAMMATIC_SCROLL_MS;
                if (settledFrameCount >= SCROLL_SETTLED_FRAME_COUNT || hasTimedOut) {
                    headerScroll.scrollLeft = bodyScroll.scrollLeft;
                    isProgrammaticScrollRef.current = false;
                    scrollSettleFrameRef.current = null;
                    return;
                }

                scrollSettleFrameRef.current = requestAnimationFrame(checkIfSettled);
            };

            scrollSettleFrameRef.current = requestAnimationFrame(checkIfSettled);
        };

        const raf = requestAnimationFrame(doScroll);
        return () => {
            cancelAnimationFrame(raf);
            if (scrollSettleFrameRef.current !== null) {
                cancelAnimationFrame(scrollSettleFrameRef.current);
                scrollSettleFrameRef.current = null;
                isProgrammaticScrollRef.current = false;
            }
        };
    }, [props.selectedDate, dayColumns, props.startDate, props.endDate]);

    useEffect(() => {
        if (!scrollContainerRef.current) return;

        if (props.highlightedMeetingId) {
            const meetingElement = document.getElementById(
                `event-${props.highlightedMeetingId}`
            );
            if (meetingElement && scrollContainerRef.current) {
                setTimeout(() => {
                    const container = scrollContainerRef.current;
                    if (!container) return;

                    const containerRect = container.getBoundingClientRect();
                    const elementRect = meetingElement.getBoundingClientRect();
                    const elementTopRelativeToContainer =
                        elementRect.top -
                        containerRect.top +
                        container.scrollTop;
                    const elementHeight = elementRect.height;
                    const elementCenter =
                        elementTopRelativeToContainer + elementHeight / 2;
                    const containerHeight = container.clientHeight;
                    const targetScrollTop = elementCenter - containerHeight / 2;
                    const maxScroll = container.scrollHeight - containerHeight;
                    const clampedScroll = Math.max(
                        0,
                        Math.min(targetScrollTop, maxScroll)
                    );

                    container.scrollTo({
                        top: clampedScroll,
                        behavior: 'smooth',
                    });
                }, 300);
            }
        } else if (props.shouldScrollToCurrentTime) {
            const currentTimePos = getCurrentTimePosition();

            if (currentTimePos !== null && scrollContainerRef.current) {
                const containerHeight = scrollContainerRef.current.clientHeight;
                const scrollPosition = currentTimePos - containerHeight / 2;
                const maxScroll =
                    scrollContainerRef.current.scrollHeight - containerHeight;
                const clampedScroll = Math.max(
                    0,
                    Math.min(scrollPosition, maxScroll)
                );

                scrollContainerRef.current.scrollTo({
                    top: clampedScroll,
                    behavior: 'smooth',
                });
            }
        }
    }, [props.highlightedMeetingId, props.shouldScrollToCurrentTime]);

    const handleEventClick = (event: CalendarObject) => {
        // Always allow selecting an event to view its info; selectionDisabled only blocks empty-slot clicks (creating new)
        props.setSelectedEvent?.(event);
    };

    const navigateToDocumentDiscussion = async (
        commsId: number,
        discussionId: number
    ) => {
        const commsHubTab = isBuyer
            ? BuyerOppMarketplaceTab.CommsHub
            : SellerMarketplaceTab.CommsHub;

        if (isBuyer) {
            navigate(
                `${paths.venues.connect.opportunities}?opportunityId=${props.engagementId}&tab=${commsHubTab}&subtab=${MarketplaceCommsHubTab.BuyerCommunications}&${QueryParams.CommsIdKey}=${commsId}&${QueryParams.DiscussionIdKey}=${discussionId}`
            );
        } else {
            navigate(
                `${paths.venues.connect.engagement(props.engagementId)}?tab=${commsHubTab}&subtab=${MarketplaceCommsHubTab.BuyerCommunications}&${QueryParams.CommsIdKey}=${commsId}&${QueryParams.DiscussionIdKey}=${discussionId}`
            );
        }
    };

    const handleEmptySlotClick = (date: Date, hour: number) => {
        if (props.selectionDisabled) {
            return;
        }
        if (
            !(
                canBuyerTeamMemberManageMeetings(summary, props.engagementId) ||
                canSellerTeamMemberManageMeetings(summary, props.engagementId)
            )
        ) {
            return;
        }
        const startTime = moment(date).hour(hour).minute(0).second(0).toDate();
        const endTime = moment(date)
            .hour(hour + 1)
            .minute(0)
            .second(0)
            .toDate();

        if (props.onEmptySlotClick) {
            props.onEmptySlotClick(date, startTime, endTime);
        }
    };
    const isToday = (date: Date): boolean => {
        const today = moment().startOf('day');
        const checkDate = moment(date).startOf('day');
        return today.isSame(checkDate, 'day');
    };

    const getCurrentTimePosition = (): number | null => {
        const now = moment();
        const hours = now.hours();
        const minutes = now.minutes();
        return hours * HOUR_HEIGHT_PX + (minutes / 60) * HOUR_HEIGHT_PX;
    };

    const todayColumnIndex = useMemo(() => {
        return dayColumns.findIndex((day) => isToday(day.date));
    }, [dayColumns]);

    const [currentTimePosition, setCurrentTimePosition] = useState<
        number | null
    >(getCurrentTimePosition());

    useEffect(() => {
        const updateColumnWidth = () => {
            setColumnWidth(window.innerWidth <= 768 ? 280 : 350);
        };

        updateColumnWidth();
        window.addEventListener('resize', updateColumnWidth);

        return () => {
            window.removeEventListener('resize', updateColumnWidth);
        };
    }, []);

    useEffect(() => {
        const updateCurrentTime = () => {
            setCurrentTimePosition(getCurrentTimePosition());
        };

        updateCurrentTime();
        const interval = setInterval(updateCurrentTime, 60000);

        return () => {
            clearInterval(interval);
        };
    }, []);

    const hadleCancelClick = (event: MeetingObject) => {
        if (!event.meetingProposalId) return;

        setPendingProposal(event);
        setShowCancelConfirmation(true);
    };

    const getEventFormatString = (event: CalendarObject): string => {
        if (isAvailabilityBlockObject(event)) {
            return (
                event.formats
                    ?.map((format) => MeetingFormatTypesOptions[format.format])
                    .join(', ') || ''
            );
        }
        const meeting = event as MeetingObject;
        return meeting.format ? MeetingFormatTypesOptions[meeting.format] : '';
    };

    const getEventClassName = (event: CalendarObject): string => {
        const isAvailabilityBlock = isAvailabilityBlockObject(event);
        const isSelected = isAvailabilityBlock
            ? (props.selectedEvent as AvailabilityBlockObject)
                  ?.availabilityBlockId ===
              (event as AvailabilityBlockObject).availabilityBlockId
            : !isAvailabilityBlockObject(props.selectedEvent) &&
              ((props.selectedEvent as MeetingObject)?.meetingProposalId != null
                  ? (props.selectedEvent as MeetingObject).meetingProposalId ===
                    (event as MeetingObject).meetingProposalId
                  : props.selectedEvent?.id === event.id);
        const isHighlighted =
            !isAvailabilityBlock && props.highlightedMeetingId === event.id;

        let className = 'event-block';

        if (isAvailabilityBlockObject(event)) {
            className += ' availability-block';
        } else if (isOtherGhostMeetingObject(event)) {
            className += ' other-ghost-meeting';
        }
        if (isSelected) className += ' selected';
        if (isHighlighted) className += ' highlighted';

        if (!isAvailabilityBlockObject(event)) {
            const meeting = event as MeetingObject;
            const isUnconfirmedProposal =
                (meeting.isMeetingProposal === true ||
                    meeting.proposalStatus != null) &&
                meeting.proposalStatus !== MeetingProposalStatus.Confirmed;

            if (meeting.isCancelled) {
                className += ' proposal-cancelled';
            } else if (isUnconfirmedProposal) {
                className += ' proposal-pending';
            }

            if (
                meeting.participantsType &&
                props.viewMode !== 'buyerTopLevel'
            ) {
                if (meeting.engagementId !== props.engagementId) {
                    className += ' other-deals';
                }

                // Use participant-type color only when confirmed (so unconfirmed proposals keep orange)
                if (!isUnconfirmedProposal) {
                    switch (meeting.participantsType) {
                        case MeetingParticipantsType.SellerBuyer:
                            className += ' seller-buyer';
                            break;
                        case MeetingParticipantsType.SellerAdvisor:
                            className += ' seller-advisor';
                            break;
                        case MeetingParticipantsType.SellerAdminSupport:
                            className += ' seller-admin-support';
                            break;
                        case MeetingParticipantsType.BuyerAdminSupport:
                            className += ' buyer-admin-support';
                            break;
                    }
                }
            }
        }

        return className;
    };

    const mapColorCodeStyle = (format: MeetingFormat): String => {
        switch (format) {
            case MeetingFormat.InPerson:
                return 'color-white';
            case MeetingFormat.Virtual:
                return 'color-black';
            case MeetingFormat.Mixed:
                return 'color-gray';
            default:
                return '';
        }
    };

    const mapMeetingTile = (
        event: MeetingObject,
        eventStart: moment.Moment,
        eventEnd: moment.Moment,
        top: number,
        height: number,
        widthPercent: number,
        leftPercent: number
    ): JSX.Element | null => {
        let titleString =
            props.party === party && isBuyer
                ? event.engagementId
                    ? `[OppId ${event.engagementId}]`
                    : `${event.name}`
                : event.name;

        const buyerName = getBuyerName(event);
        if (titleString == '') {
            titleString = `Meeting with ${buyerName}`;
        }

        const timeRange = MeetingTimeZoneHelper.formatTimeRange(
            event.dateStart,
            event.dateEnd
        );
        const scheduledTimeNote = MeetingTimeZoneHelper.getScheduledTimeNote(
            event.dateStart,
            event.dateEnd,
            event.timeZone
        );
        const tooltipText = [titleString, timeRange, scheduledTimeNote]
            .filter(Boolean)
            .join('\n');

        const shouldShowLocation = event.format !== MeetingFormat.Virtual;
        let locationText: string | null = null;
        if (shouldShowLocation) {
            if (event.address) {
                locationText = `${event.address.city || 'N/A'}, ${
                    event.address.state || 'N/A'
                }`;
            } else {
                locationText = 'N/A';
            }
        }

        const isSelected = props.selectedEvent === event;
        const MIN_EXPANDED_HEIGHT = 120;
        const shouldExpand = isSelected && height < MIN_EXPANDED_HEIGHT;
        const displayHeight = shouldExpand ? MIN_EXPANDED_HEIGHT : height;

        return (
            <div
                key={event.id}
                id={`event-${event.id}`}
                className={getEventClassName(event)}
                style={{
                    top: `${top}px`,
                    height: `${displayHeight}px`,
                    left: `${leftPercent}%`,
                    width: `${widthPercent}%`,
                    zIndex: isSelected ? 50 : undefined,
                }}
                onClick={(e) => {
                    e.stopPropagation();
                    handleEventClick(event);
                }}
                title={tooltipText}
                data-tooltip-title={titleString}
                data-tooltip-time={timeRange}>
                {props.getNotificationFlagCodeForEvent &&
                    (() => {
                        const flagCode =
                            props.getNotificationFlagCodeForEvent(event);
                        return flagCode ? (
                            <div
                                className="week-view-event-notification-flag"
                                onClick={(e) => e.stopPropagation()}>
                                <NotificationFlag
                                    flagClassName="nf-flag"
                                    eventCode={flagCode}
                                    includePrefix={true}
                                    deletePrefixMatches={true}
                                />
                            </div>
                        ) : null;
                    })()}
                <div
                    className={`event-content${
                        !props.viewMode.endsWith('TopLevel') &&
                        !event.isOtherGhostMeeting
                            ? ' pr-30'
                            : ''
                    }`}>
                    <div className={'event-content-row'}>
                        <div className="event-title">{titleString}</div>
                        <div
                            className={`event-format ${mapColorCodeStyle(event.format)}`}>
                            {getEventFormatString(event)}
                        </div>
                    </div>
                    <div className={'event-content-row'}>
                        {shouldShowLocation && (
                            <div
                                className={`event-location ${mapColorCodeStyle(event.format)}`}>
                                {locationText}
                            </div>
                        )}
                        <div className="event-time">{timeRange}</div>
                    </div>

                    {event.isCancelled && (
                        <div className="event-cancelled-label">Cancelled</div>
                    )}

                    {(() => {
                            if (event.isMeetingProposal && (
                                event.proposalStatus !== MeetingProposalStatus.Confirmed ||
                                event.isCancelled
                            )) return null;

                            const meetingParticipants =
                                event.meetingParticipants || [];

                            const sellerParticipants = meetingParticipants.filter(
                                (p) => p.party === TimelineParty.Seller
                            );
                            const buyerParticipants = meetingParticipants.filter(
                                (p) => p.party === TimelineParty.Buyer
                            );

                            if (
                                sellerParticipants.length === 0 &&
                                buyerParticipants.length === 0
                            )
                                return null;

                            const showBadge =
                                event.format === MeetingFormat.Mixed;
                            return (
                                <div className="event-participants">
                                    <div className="participants-column">
                                        <div className="participants-column-header">
                                            {!isBuyer
                                                ? 'Our Attendees'
                                                : props.practiceName ||
                                                  'Seller Attendees'}
                                        </div>
                                        {sellerParticipants.map((p, i) => {
                                            const badge = p.attendanceMode === MeetingAttendanceMode.InPerson ? '(IP)' : '(V)';

                                            return (
                                                <div
                                                    key={i}
                                                    className="participant-entry"
                                                    title={
                                                        p.name +
                                                        (showBadge
                                                            ? ' ' + badge
                                                            : '')
                                                    }>
                                                    <span className="participant-name">
                                                        {p.name}
                                                    </span>
                                                    {showBadge && (
                                                        <span
                                                            className="participant-badge white">
                                                            {badge}
                                                        </span>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <div className="participants-column">
                                        <div className="participants-column-header">
                                            {!isBuyer
                                                ? buyerName || 'Buyer Attendees'
                                                : 'Our Attendees'}
                                        </div>
                                        {buyerParticipants.map((p, i) => {
                                            const badge = p.attendanceMode === MeetingAttendanceMode.InPerson ? '(IP)' : '(V)';

                                            return (
                                                <div
                                                    key={i}
                                                    className="participant-entry"
                                                    title={
                                                        p.name +
                                                        (showBadge
                                                            ? ' ' + badge
                                                            : '')
                                                    }>
                                                    <span className="participant-name">
                                                        {p.name}
                                                    </span>
                                                    {showBadge && (
                                                        <span
                                                            className="participant-badge white">
                                                            {badge}
                                                        </span>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })()}

                    {!event.isOtherGhostMeeting && (
                        <button
                            className="event-full-info-link"
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelectedMeetingForFullInfo(event);
                            }}>
                            Show full info
                        </button>
                    )}
                </div>
                {!props.viewMode.endsWith('TopLevel') &&
                    !event.isOtherGhostMeeting &&
                    (() => {
                        const isMeetingProposal =
                            event.isMeetingProposal === true;
                        const proposalStatus = event.proposalStatus;
                        const party = props.party;

                        const shouldShowProposalActions =
                            isMeetingProposal &&
                            !event.isCancelled &&
                            ((party === TimelineParty.Seller &&
                                proposalStatus ===
                                    MeetingProposalStatus.PendingSellerAnswer) ||
                                (isBuyer &&
                                    proposalStatus ===
                                        MeetingProposalStatus.PendingBuyerAnswer));

                        if (shouldShowProposalActions) {
                            const canManage =
                                canBuyerTeamMemberManageMeetings(
                                    summary,
                                    props.engagementId
                                ) ||
                                canSellerTeamMemberManageMeetings(
                                    summary,
                                    props.engagementId
                                );
                            const isSellerTMWithoutCommsHub =
                                isSellerTeamMemberForEngagement(
                                    summary,
                                    props.engagementId
                                ) &&
                                !canSellerTeamMemberManageMeetings(
                                    summary,
                                    props.engagementId
                                );
                            return (
                                <div
                                    className="event-actions proposal-actions"
                                    onClick={(e) => e.stopPropagation()}>
                                    {!isSellerTMWithoutCommsHub &&
                                        !hasMeetingStarted(event) && (
                                        <button
                                            className="event-action-btn"
                                            onClick={() => {
                                                if (event.meetingProposalId) {
                                                    if (
                                                        props.party === party &&
                                                        isBuyer &&
                                                        summary.roleId !==
                                                            USER_ROLES.PRACTICE_SECONDARY_USER &&
                                                        props.onBuyerProposalAccept
                                                    ) {
                                                        props.onBuyerProposalAccept(
                                                            event.meetingProposalId
                                                        );
                                                    } else {
                                                        setPendingProposal(
                                                            event
                                                        );
                                                        setShowApproveConfirmation(
                                                            true
                                                        );
                                                    }
                                                }
                                            }}
                                            title="Accept Proposal">
                                            <FontAwesomeIcon icon={faCheck} />
                                        </button>
                                    )}
                                    {canManage && (
                                        <button
                                            className="event-action-btn proposal-reject-btn"
                                            onClick={() =>
                                                hadleCancelClick(event)
                                            }
                                            title="Reject Proposal">
                                            <FontAwesomeIcon icon={faTimes} />
                                        </button>
                                    )}
                                    {canManage &&
                                        !hasMeetingStarted(event) &&
                                        !(
                                            isSellerTeamMemberForEngagement(
                                                summary,
                                                props.engagementId
                                            ) &&
                                            proposalStatus ===
                                                MeetingProposalStatus.PendingBuyerAnswer
                                        ) && (
                                            <button
                                                className="event-action-btn"
                                                onClick={() =>
                                                    props.onEditClick?.(
                                                        event.meetingProposalId !=
                                                            null
                                                            ? event.meetingProposalId
                                                            : event.id
                                                    )
                                                }
                                                title="Edit Proposal">
                                                <FontAwesomeIcon
                                                    icon={faEdit}
                                                />
                                            </button>
                                        )}
                                </div>
                            );
                        }

                        if (event.isCancelled) {
                            return <></>;
                        }

                        const shouldNotShowProposalActions =
                            isMeetingProposal &&
                            ((party !== TimelineParty.Seller &&
                                proposalStatus ===
                                    MeetingProposalStatus.PendingSellerAnswer) ||
                                (!isBuyer &&
                                    proposalStatus ===
                                        MeetingProposalStatus.PendingBuyerAnswer));

                        if (shouldNotShowProposalActions) {
                            return <></>;
                        }

                        return (
                            <div
                                className="event-actions"
                                onClick={(e) => e.stopPropagation()}>
                                {!(event.participantsType === MeetingParticipantsType.SellerBuyer && props.isSfas && (props.party === TimelineParty.Seller || !props.isActiveBroker)) && (
                                    <button
                                        className="event-action-btn"
                                        onClick={() =>
                                            navigateToDocumentDiscussion(
                                                event.commsId,
                                                event.threadedDiscussionId
                                            )
                                        }
                                        title="Meeting Discussion">
                                        <FontAwesomeIcon icon={faCommentDots} />
                                    </button>
                                )}
                                {!(isAdmin || isBroker || isObserver) &&
                                    proposalStatus !==
                                        MeetingProposalStatus.Confirmed &&
                                    (canBuyerTeamMemberManageMeetings(
                                        summary,
                                        props.engagementId
                                    ) ||
                                        canSellerTeamMemberManageMeetings(
                                            summary,
                                            props.engagementId
                                        )) && (
                                        <button
                                            className="event-action-btn"
                                            onClick={() =>
                                                props.onEditClick?.(
                                                    (event as any)
                                                        .meetingProposalId !=
                                                        null
                                                        ? (event as any)
                                                              .meetingProposalId
                                                        : event.id
                                                )
                                            }
                                            title="Edit">
                                            <FontAwesomeIcon icon={faEdit} />
                                        </button>
                                    )}
                                <button
                                        className="event-action-btn"
                                        onClick={() => {
                                            setSelectedMeetingForParticipants(
                                                event
                                            );
                                            setIsParticipantsModalVisible(true);
                                        }}
                                        title="Manage Attendees">
                                        <FontAwesomeIcon icon={faUser} />
                                    </button>
                                {!(isAdmin || isBroker || isObserver) &&
                                    proposalStatus ===
                                        MeetingProposalStatus.Confirmed && (
                                        <button
                                            className="event-action-btn"
                                            onClick={() => {
                                                navigate(
                                                    `/marketplace/meetings?engagementId=${props.engagementId}&meetingId=${event.id}`
                                                );
                                            }}
                                            title="Go to scheduled meetings tab">
                                            <FontAwesomeIcon
                                                icon={faCalendar}
                                            />
                                        </button>
                                    )}
                                {!(isAdmin || isBroker || isObserver) &&
                                    (canBuyerTeamMemberManageMeetings(
                                        summary,
                                        props.engagementId
                                    ) ||
                                        canSellerTeamMemberManageMeetings(
                                            summary,
                                            props.engagementId
                                        )) &&
                                    !hasMeetingStarted(event) && (
                                        <button
                                            className="event-action-btn proposal-reject-btn"
                                            onClick={() =>
                                                hadleCancelClick(event)
                                            }
                                            title="Cancel Meeting">
                                            <FontAwesomeIcon icon={faTimes} />
                                        </button>
                                    )}
                            </div>
                        );
                    })()}
            </div>
        );
    };

    const mapAvailabilityTile = (
        event: AvailabilityBlockObject,
        eventStart: moment.Moment,
        eventEnd: moment.Moment,
        top: number,
        height: number,
        widthPercent: number,
        leftPercent: number
    ): JSX.Element | null => {
        const titleString = 'Availability Block';
        const timeRange = MeetingTimeZoneHelper.formatTimeRange(
            event.dateStart,
            event.dateEnd
        );
        const tooltipText = `${titleString}\n${timeRange}`;

        return (
            <div
                key={event.id}
                id={`event-${event.id}`}
                className={getEventClassName(event)}
                style={{
                    top: `${top}px`,
                    height: `${height}px`,
                    left: `${leftPercent}%`,
                    width: `${widthPercent}%`,
                }}
                onClick={(e) => {
                    e.stopPropagation();
                    handleEventClick(event);
                }}
                title={tooltipText}
                data-tooltip-title={titleString}
                data-tooltip-time={timeRange}>
                <div className="event-time">{timeRange}</div>
                <div className="event-format">
                    {getEventFormatString(event)}
                </div>
                <div
                    className="availability-block-status"
                    style={{ display: 'none' }}>
                    Open / OnHold
                </div>
            </div>
        );
    };

    const mapEvents = (
        event: CalendarObject,
        dayEvents: CalendarObject[],
        slotHour: number
    ): JSX.Element | null => {
        if (moment(event.dateStart).hour() !== slotHour) {
            return null;
        }

        const eventStart = moment(event.dateStart);
        const eventEnd = moment(event.dateEnd);
        const durationHours = eventEnd.diff(eventStart, 'hours', true);
        const startMinutes = eventStart.minute();

        const top = (startMinutes / 60) * HOUR_HEIGHT_PX;
        const height = durationHours * HOUR_HEIGHT_PX;

        // for overlapping events
        const { column, totalColumns, span } = getEventPosition(
            event,
            dayEvents
        );
        const widthPercent =
            totalColumns > 1 ? (span / totalColumns) * 100 - 2 : 100;
        const leftPercent =
            totalColumns > 1 ? column * (100 / totalColumns) + 1 : 0;

        if (isAvailabilityBlockObject(event)) {
            return mapAvailabilityTile(
                event,
                eventStart,
                eventEnd,
                top,
                height,
                widthPercent,
                leftPercent
            );
        } else {
            return mapMeetingTile(
                event as MeetingObject,
                eventStart,
                eventEnd,
                top,
                height,
                widthPercent,
                leftPercent
            );
        }
    };

    return (
        <div className="meeting-scheduling-weekly-view">
            {props.viewMode === 'opportunity' &&
            (canBuyerTeamMemberManageMeetings(summary, props.engagementId) ||
                canSellerTeamMemberManageMeetings(
                    summary,
                    props.engagementId
                )) ? (
                <div className="switcher-wrapper">
                    <div className="d-flex align-items-center">
                        <ReactSwitch
                            disabled={false}
                            className="mr-2"
                            offColor="#d92550"
                            checked={showScheduledMeetings}
                            onChange={(checked) =>
                                setShowScheduledMeetings?.(checked)
                            }
                            height={22}
                            handleDiameter={16}
                        />
                        Show All Meetings
                        {props.isLoadingGhostMeetings && (
                            <div className="ghost-meetings-loader ml-2">
                                <div className="loader-inner ball-clip-rotate">
                                    <div className="ghost-meetings-loader-spinner" />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            ) : null}
            {props.showPostAvailabilityButton && (
                <>
                    <div className="switcher-wrapper">
                        <div className="d-flex align-items-center">
                            <button
                                className="btn btn-primary"
                                disabled={props.isPostingAvailability}
                                onClick={() =>
                                    setShowPostAvailabilityConfirmation(true)
                                }>
                                Post availability
                            </button>
                        </div>
                    </div>
                    <ConfirmationModal
                        isVisible={showPostAvailabilityConfirmation}
                        setIsVisible={setShowPostAvailabilityConfirmation}
                        header="Post Availability"
                        confirmationText="Are you sure you want to post your availability? This will complete the 'Provide Meeting Availability' event."
                        nextButtonText="Post"
                        cancelButtonText="Cancel"
                        isLoading={props.isPostingAvailability}
                        closeOnConfirm={false}
                        onConfirm={() => {
                            props.onPostAvailabilityClick?.();
                            setShowPostAvailabilityConfirmation(false);
                        }}
                    />
                </>
            )}
            <div className="week-calendar-header">
                <div className="time-gutter-sticky">{timezone}</div>
                <div className="days-header-scrollable" ref={headerScrollRef}>
                    <div className="days-header">
                        {dayColumns.map((day, index) => {
                            const isTodayColumn = isToday(day.date);
                            const hasOverlaps = hasDayOverlappingMeetings(
                                day.date
                            );
                            return (
                                <div
                                    key={index}
                                    className={`day-header ${isTodayColumn ? 'today' : ''} ${hasOverlaps ? 'has-overlapping-meetings' : ''}`}
                                    onClick={
                                        hasOverlaps
                                            ? () =>
                                                  setOverlappingDayPopupDate(
                                                      day.date
                                                  )
                                            : undefined
                                    }>
                                    <div className="day-name">
                                        {day.dayName}
                                    </div>
                                    <div className="day-date-row">
                                        <div className="day-date">
                                            {day.monthDay}
                                        </div>
                                        {hasOverlaps && (
                                            <InfoTooltip
                                                idText={`overlap-${moment(day.date).format('YYYY-MM-DD')}`}
                                                text=""
                                                html={getDayOverlapTooltipHtml(
                                                    day.date
                                                )}
                                                place="bottom"
                                            />
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            <div
                className="week-calendar-body"
                ref={(el) => {
                    scrollContainerRef.current = el;
                    bodyScrollRef.current = el;
                }}>
                <div className="time-slots-container-wrapper">
                    <div className="time-labels-column">
                        {timeSlots.map((slot) => (
                            <div key={slot.hour} className="time-label-sticky">
                                {slot.label}
                            </div>
                        ))}
                    </div>
                    <div className="days-scrollable-container">
                        <div className="time-slots-container">
                            {todayColumnIndex !== -1 &&
                                currentTimePosition !== null && (
                                    <div
                                        className="current-time-indicator-wrapper"
                                        style={{
                                            left: `${
                                                todayColumnIndex * columnWidth
                                            }px`, // Position at today's column
                                            top: `${currentTimePosition}px`,
                                            width: `${columnWidth}px`,
                                        }}>
                                        <div className="current-time-dot" />
                                        <div className="current-time-line" />
                                    </div>
                                )}
                            {timeSlots.map((slot) => (
                                <div key={slot.hour} className="time-row">
                                    <div className="time-slots">
                                        {dayColumns.map((day, dayIndex) => {
                                            const events = getEventsForSlot(
                                                day.date,
                                                slot.hour
                                            );
                                            const dayEvents = getEventsForDay(
                                                day.date
                                            );

                                            // Render availability blocks first, then meetings, so meetings are on top and receive clicks when they overlap
                                            const sortedEvents = [
                                                ...events,
                                            ].sort((a, b) => {
                                                const aIsBlock =
                                                    isAvailabilityBlockObject(
                                                        a
                                                    );
                                                const bIsBlock =
                                                    isAvailabilityBlockObject(
                                                        b
                                                    );
                                                const aIsOtherGhost =
                                                    isOtherGhostMeetingObject(
                                                        a
                                                    );
                                                const bIsOtherGhost =
                                                    isOtherGhostMeetingObject(
                                                        b
                                                    );

                                                if (aIsBlock && !bIsBlock)
                                                    return -1;
                                                if (!aIsBlock && bIsBlock)
                                                    return 1;

                                                if (
                                                    aIsOtherGhost &&
                                                    !bIsOtherGhost &&
                                                    !bIsBlock
                                                )
                                                    return -1;
                                                if (
                                                    !aIsOtherGhost &&
                                                    bIsOtherGhost &&
                                                    !aIsBlock
                                                )
                                                    return 1;

                                                return 0;
                                            });

                                            const isTodayColumn = isToday(
                                                day.date
                                            );
                                            const slotStart = moment(day.date)
                                                .hour(slot.hour)
                                                .minute(0)
                                                .second(0);
                                            const slotEnd = moment(day.date)
                                                .hour(slot.hour + 1)
                                                .minute(0)
                                                .second(0);
                                            const isSelectedSlot =
                                                props.selectedTimeSlot &&
                                                moment(
                                                    props.selectedTimeSlot
                                                        .startTime
                                                ).isSame(slotStart) &&
                                                moment(
                                                    props.selectedTimeSlot
                                                        .endTime
                                                ).isSame(slotEnd);
                                            const hasSelectedEvent = (() => {
                                                if (!props.selectedEvent) return false;
                                                const sel = props.selectedEvent;
                                                const slotStartMs = slotStart.valueOf();
                                                const slotEndMs = slotEnd.valueOf();
                                                const evStart = new Date(sel.dateStart).getTime();
                                                const evEnd = new Date(sel.dateEnd).getTime();
                                                return evStart < slotEndMs && evEnd > slotStartMs;
                                            })();
                                            return (
                                                <div
                                                    key={dayIndex}
                                                    className={`time-slot ${isTodayColumn ? 'today' : ''}${isSelectedSlot ? ' selected-slot' : ''}${hasSelectedEvent ? ' has-selected-event' : ''}`}
                                                    style={undefined}
                                                    onClick={() =>
                                                        handleEmptySlotClick(
                                                            day.date,
                                                            slot.hour
                                                        )
                                                    }>
                                                    {sortedEvents.map((event) =>
                                                        mapEvents(
                                                            event,
                                                            dayEvents,
                                                            slot.hour
                                                        )
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {showApproveConfirmation && (
                <ConfirmationModal
                    isVisible={showApproveConfirmation}
                    setIsVisible={setShowApproveConfirmation}
                    header="Approve Meeting Proposal"
                    confirmationText="Are you sure you want to approve this meeting proposal?"
                    onConfirm={async () => {
                        if (
                            pendingProposal?.meetingProposalId &&
                            props.onProposalAccept
                        ) {
                            setIsApproving(true);
                            try {
                                await props.onProposalAccept(
                                    pendingProposal?.meetingProposalId
                                );
                                setShowApproveConfirmation(false);
                                setPendingProposal(undefined);
                            } catch (error) {
                            } finally {
                                setIsApproving(false);
                            }
                        }
                    }}
                    nextButtonText="Confirm"
                    cancelButtonText="Cancel"
                    closeOnConfirm={false}
                    isLoading={isApproving}
                />
            )}

            {showCancelConfirmation && pendingProposal && (
                <EngagementMeetingCancelModal
                    isVisible={showCancelConfirmation}
                    onClose={() => setShowCancelConfirmation(false)}
                    engagementId={props.engagementId}
                    proposal={pendingProposal}
                    isBuyer={isBuyer}
                    isSfas={props.isSfas}
                    isCancelling={isCancelling}
                    onCancel={async () => {
                        if (
                            pendingProposal?.meetingProposalId &&
                            props.onProposalCancel
                        ) {
                            setIsCancelling(true);
                            try {
                                await props.onProposalCancel(
                                    pendingProposal?.meetingProposalId
                                );
                                setShowCancelConfirmation(false);
                                setPendingProposal(undefined);
                                props.onRefreshMeetings?.();
                            } catch (error) {
                            } finally {
                                setIsCancelling(false);
                            }
                        }
                    }}
                />
            )}

            {selectedMeetingForParticipants &&
                props.engagementId &&
                selectedMeetingForParticipants.meetingProposalId && (
                    <MeetingParticipantsModal
                        isVisible={isParticipantsModalVisible}
                        onClose={() => {
                            setIsParticipantsModalVisible(false);
                            setSelectedMeetingForParticipants(null);
                            if (props.onRefreshMeetings) {
                                props.onRefreshMeetings();
                            }
                        }}
                        engagementId={props.engagementId}
                        meetingId={
                            selectedMeetingForParticipants.meetingProposalId
                        }
                        meetingFormat={selectedMeetingForParticipants.format}
                        meetingStartDate={
                            selectedMeetingForParticipants.dateStart
                        }
                        meetingStatus={selectedMeetingForParticipants.status}
                        isReadOnly={
                            isEngagementLocked ||
                            props.party === TimelineParty.VetValue ||
                            props.party === TimelineParty.Broker ||
                            !(
                                canBuyerTeamMemberJoinMeeting(
                                    summary,
                                    props.engagementId
                                ) ||
                                canSellerTeamMemberManageMeetings(
                                    summary,
                                    props.engagementId
                                )
                            ) ||
                            ((selectedMeetingForParticipants.status ===
                                EngagementMeetingStatus.InProgress ||
                                selectedMeetingForParticipants.status ===
                                    EngagementMeetingStatus.Completed) &&
                                !canSellerTeamMemberManageMeetings(
                                    summary,
                                    props.engagementId
                                ))
                        }
                    />
                )}

            {selectedMeetingForFullInfo && (
                <EngagementMeetingFullInfoModal
                    isVisible={true}
                    onClose={() => setSelectedMeetingForFullInfo(null)}
                    event={selectedMeetingForFullInfo}
                    party={party}
                    buyerName={getBuyerName(selectedMeetingForFullInfo)}
                    practiceName={props.practiceName}
                />
            )}

            {overlappingDayPopupDate && (
                <OverlappingDayCalendarView
                    isVisible={true}
                    onClose={() => setOverlappingDayPopupDate(null)}
                    date={overlappingDayPopupDate}
                    events={getEventsForDay(overlappingDayPopupDate)}
                    selectedEvent={props.selectedEvent}
                    setSelectedEvent={props.setSelectedEvent}
                    highlightedMeetingId={props.highlightedMeetingId}
                    viewMode={props.viewMode}
                    party={props.party}
                    engagementId={props.engagementId}
                    isSfas={props.isSfas}
                    isActiveBroker={props.isActiveBroker}
                    onEditClick={props.onEditClick}
                    onProposalAccept={props.onProposalAccept}
                    onProposalCancel={props.onProposalCancel}
                    onRefreshMeetings={props.onRefreshMeetings}
                    getNotificationFlagCodeForEvent={
                        props.getNotificationFlagCodeForEvent
                    }
                    selectedBuyers={props.selectedBuyers}
                    practiceName={props.practiceName}
                />
            )}
        </div>
    );
};
